import { useEffect, useMemo, useState } from 'react'

import styled from 'styled-components'

import { type ReferralRole, useReferrals } from 'api/referrals/useReferrals'
import { type Referral } from 'api/types/referrals'
import { TABS } from 'pages/referrals'
import { useReferralsListCacheStore } from 'store/referralsListCacheStore'

import { FanItem } from '../FanItem'

interface SummaryProps {
  tab: string
}

const normalizeReferral = (r: Referral): Referral => {
  const anyR = r as any
  const avatar = anyR?.avatar ?? {}
  const cover = anyR?.cover ?? {}
  const coverObj = Array.isArray(cover) ? {} : cover

  return {
    ...r,
    avatar: {
      ...avatar,
      url: String(avatar?.url ?? ''),
      created_at: String(avatar?.created_at ?? ''),
    },
    cover: {
      ...coverObj,
      url: String(coverObj?.url ?? ''),
    },
  } satisfies Referral
}

export const Summary = ({ tab }: SummaryProps) => {
  const getUsers = useReferralsListCacheStore(state => state.getUsers)
  const setUsers = useReferralsListCacheStore(state => state.setUsers)

  const role: ReferralRole = tab === TABS.CREATORS ? 'creator' : 'fan'

  const cachedForTab = useMemo(() => getUsers(tab), [getUsers, tab])
  const hasCache = cachedForTab.length > 0

  const [renderList, setRenderList] = useState<Referral[]>(cachedForTab)

  useEffect(() => {
    setRenderList(cachedForTab)
  }, [cachedForTab])

  const { data: referrals } = useReferrals(role)

  useEffect(() => {
    if (!referrals) return

    const list: Referral[] = Array.isArray(referrals)
      ? referrals
      : ((referrals as any)?.data as Referral[]) ?? []

    if (!Array.isArray(list)) return

    const normalized = list.map(normalizeReferral)

    // API already filters by role, so cache only the current tab.
    setUsers(tab, normalized)
    setRenderList(normalized)
  }, [referrals, tab, setUsers])

  if (!hasCache && renderList.length === 0 && !referrals) return null

  return (
    <ItemsContainer>
      {renderList.map(referral => (
        <FanItem
          key={referral.id}
          fan={referral}
          isCreator={tab === TABS.CREATORS}
        />
      ))}
    </ItemsContainer>
  )
}

const ItemsContainer = styled.div``